import { useQuery } from '@tanstack/react-query';

import { API_ENDPOINTS } from '@/config/api';
import { apiClient } from '@/services/api/client';
import { FOOTER_DESCRIPTION_SLUG, type FooterDescriptionContent } from '@/types/footer-description';
import type { BackendApiResponse } from '@/types/general-enquiry';

import { mergeWithDefaults, type BackendFooterDescription } from '../utils';

export const footerDescriptionQueryKeys = {
  all: ['footer-description'] as const,
  public: ['footer-description', 'public'] as const,
};

/**
 * Public fetch for the editable footer description. Mirrors the
 * `useFaq` / `usePrivacy` / `useTerms` hooks so the same caching and
 * revalidation conventions apply. Returns `null` when the backend has
 * no record yet — the wrapper renders the static fallback in that
 * case.
 */
export const useFooterDescription = (
  initialData?: FooterDescriptionContent,
  options?: { enabled?: boolean },
) => {
  return useQuery({
    queryKey: footerDescriptionQueryKeys.public,
    queryFn: async () => {
      const { data } = await apiClient.get<BackendApiResponse<BackendFooterDescription | null>>(
        API_ENDPOINTS.footerDescription.get(FOOTER_DESCRIPTION_SLUG),
        { skipSuccessToast: true, skipErrorToast: true },
      );
      const row = data?.data ?? null;
      return row ? mergeWithDefaults(row) : null;
    },
    initialData,
    enabled: options?.enabled ?? true,
    staleTime: initialData ? 300_000 : 60_000,
    refetchOnMount: initialData ? false : 'always',
  });
};
